import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { StudentResponses as StudentResponsesType } from '../../../../../../db/polls/types';
import { i18nNS } from '../../../../../../i18n';
import ChevronRightIcon from '../../../../../../icons/ChevronRightIcon';
import { toSnakeCase } from '../../../../../../utils/string';
import EditStudentResponsesDialog from '../EditStudentResponsesDialog';
import { Cell, CellButton, CellLabel, CellValue } from '../styles';
import useStudentResponsesVM, { Props as VMProps } from './vm';

interface Props<T extends FieldValues> extends VMProps<T> {
  onUpdateStudentResponses: (studentResponses: StudentResponsesType) => any;
}

const StudentResponses = <T extends FieldValues>({
  onUpdateStudentResponses: handleUpdateStudentResponses,
  ...vmOptions
}: Props<T>) => {
  const { t } = useTranslation([i18nNS.POLL, i18nNS.GLOSSARY]);

  const {
    canEditStudentResponses,
    controllerMethods,
    isEditStudentResponsesDialogOpen,
    onEditStudentResponses: handleEditStudentResponses,
    onCloseEditStudentResponsesDialog: handleCloseEditStudentResponsesDialog,
  } = useStudentResponsesVM(vmOptions);
  const {
    field: { value },
  } = controllerMethods;

  if (canEditStudentResponses) {
    return (
      <>
        <CellButton
          variant="text"
          endIcon={<ChevronRightIcon aria-hidden />}
          onClick={handleEditStudentResponses}
          aria-label={t('click_to_edit_student_responses', { ns: i18nNS.POLL })}
          fullWidth
        >
          <Cell width="100%">
            <CellLabel variant="h6Bold" color="grey.800">
              {t('student_response', { ns: i18nNS.POLL })}
            </CellLabel>
            <CellValue variant="h6Regular" color="grey.800">
              {t(toSnakeCase(value), { ns: i18nNS.GLOSSARY })}
            </CellValue>
          </Cell>
        </CellButton>
        <EditStudentResponsesDialog
          open={isEditStudentResponsesDialogOpen}
          onClose={handleCloseEditStudentResponsesDialog}
          studentResponses={value}
          onUpdateStudentResponses={handleUpdateStudentResponses}
        />
      </>
    );
  }

  return (
    <Cell>
      <CellLabel variant="h6Bold">{t('scoring', { ns: i18nNS.GLOSSARY })}</CellLabel>
      <CellValue variant="h6Regular">{t(value, { ns: i18nNS.GLOSSARY })}</CellValue>
    </Cell>
  );
};

export default StudentResponses;
